import cn from 'classnames';
import React from 'react';

import { IHeaderConfig } from './headerConfig';

interface TProps {
  config: IHeaderConfig;
  /** The sticky header has scrolled away from the very top of the page. */
  stuckToTop: boolean;
  children: React.ReactNode;
}

/**
 * The global header's shell: the header element itself (surface color, border,
 * positioning, scroll transitions) and the width containers around the content
 * row. Pure presentation — what renders inside the row is the caller's concern.
 */
export const HeaderFrame = ({ config, stuckToTop, children }: TProps) => {
  const { center, surface, width, sticky, animateSurface } = config;

  const isTransparent = surface === 'transparent' || surface === 'transparent-dark';
  const isWhite = surface === 'white';
  const hasSearchCenter = center.type === 'search';

  let headerStyle: React.CSSProperties | undefined;
  if (isWhite) {
    // White paints via the bg-white class, but descendants that key off
    // --page-header-bg-color (the notification dot's ring) still need the real
    // backdrop, so redeclare it here rather than duplicating the white-header
    // treatment in each page's <Head>.
    headerStyle = { '--page-header-bg-color': 'var(--page-bg-white)' } as React.CSSProperties;
  } else if (!isTransparent || stuckToTop) {
    headerStyle = { backgroundColor: 'var(--page-header-bg-color)' };
  }

  return (
    <header
      id="global-header"
      data-testid="global-header"
      style={headerStyle}
      className={cn('z-40', {
        'transition-[background-color] duration-700 ease-in-out': animateSurface,
        'absolute left-0 right-0': isTransparent,
        'transition-colors ease-linear': isTransparent && sticky,
        '!bg-transparent text-white': surface === 'transparent' && !stuckToTop,
        'text-gray-900 border-b border-gray-200': isTransparent && stuckToTop,
        'relative text-gray-900': !isTransparent,
        'bg-white border-b-0': isWhite,
      })}>
      <div
        className={cn({
          'grid grid-cols-main container-main gap-x-default': width === 'contained',
          'px-6 max-w-xxxxl lg:px-20 mx-auto': width === 'wide' || width === 'wide-contained',
        })}>
        <div
          className={cn('grid items-center gap-6 col-content h-18', {
            'grid-cols-[1fr_auto] xl:grid-cols-[1fr_auto_1fr]': hasSearchCenter,
            'grid-cols-[1fr_auto_1fr]': !hasSearchCenter,
            'mx-4 xxxl:mx-16': width === 'fluid',
            'container-main w-full': width === 'contained' || width === 'wide-contained',
          })}>
          {children}
        </div>
      </div>
    </header>
  );
};
